import { api } from '@/scripts/api';
import { helpers } from '@/scripts/helpers';
import { OpportunityHelpers } from '@/components/sliding-widgets/opportunity-details-widget/opportunity-helpers';
import createOpportunity from '@/components/sliding-widgets/opportunity-details-widget/create-opportunity';
import moment from 'moment';
import { isEmpty } from 'lodash';

export const detailsTab = {
    state: () => ({
        discountOptions: [
            {
                id: 1,
                name: '%',
                value: 'percentage'
            },

            {
                id: 2,
                name: '$',
                value: 'amount'
            }
        ]
    }),

    actions: {
        loadInitialUser({ rootState, getters }, salesPersonId) {
            const filter = `$filter=ClientId eq ${getters.clientId} and Roles/any(r: r/RoleId eq '6') and IsActive eq true and Id eq '${salesPersonId}'`;
            const top = `$top=${50}`;
            const select = '$select=Id,FirstName,LastName,ProfileImageUrl,Title,Email';
            const restUrl = `/service/Users?${filter}&${select}&${top}&$count=true`;

            return api
                .get(restUrl)
                .then((response) => {
                    for (var i = 0; i < response.data.value.length; i++) {
                        var item = {
                            id: response.data.value[i].Id,
                            name: helpers.getName(
                                response.data.value[i].FirstName,
                                response.data.value[i].LastName,
                            ),
                            profilePicture:
                                response.data.value[i].ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                            title: response.data.value[i].Title,
                            email: response.data.value[i].Email,
                        };
                        rootState.opportunity.salespersons.push(item);
                    }
                })
                .catch((error) => {
                    helpers.showFailNotification(
                        'An error occurred while fetching your sales professionals',
                        { error },
                    );
                });
        },

        loadInitialCompany({ rootState }, initialCompanyId) {
            const select = '$select=Id,Name,LogoUrl';
            const filter = `$filter=Id eq ${initialCompanyId}`;
            const restUrl = `/service/Companies?${filter}&${select}`;

            return api
                .get(restUrl)
                .then((response) => {
                    rootState.opportunity.company = {
                        id: response.data.value[0].Id,
                        name: response.data.value[0].Name,
                        imageUrl: response.data.value[0].LogoUrl
                    };
                })
                .catch((error) => {
                    helpers.showFailNotification('Failed to get company', { error });
                });
        },

        loadInitialContact({ rootState }, contactId) {
            const select = '$select=Id,Email,FirstName,LastName,ProfileImageUrl,CompanyId';
            const filter = `$filter=Id eq ${contactId}`;
            const expand = '$expand=Company($select=Id,Name,LogoUrl)';
            const restUrl = `/service/Contacts?${filter}&${expand}&${select}`;

            return api
                .get(restUrl)
                .then((response) => {
                    const contactResponse = response.data.value[0];
                    rootState.opportunity.contact = {
                        id: contactResponse.Id,
                        email: contactResponse.Email,
                        name: helpers.getName(
                            contactResponse.FirstName,
                            contactResponse.LastName
                        ),
                        imageUrl: contactResponse.ProfileImageUrl,
                        companyId: contactResponse.CompanyId
                    };

                    if (contactResponse.Company) {
                        rootState.opportunity.company = {
                            id: contactResponse.Company.Id,
                            name: contactResponse.Company.Name,
                            imageUrl: contactResponse.Company.LogoUrl
                        };
                    }
                })
                .catch((error) => {
                    helpers.showFailNotification('Failed to get contact', { error });
                });
        },

        loadMinimumGrossMargin({ commit, getters }) {
            return new Promise((resolve, reject) => {
                const service = `/api/Clients/${getters.clientId}/Account`;

                api
                    .get(service)
                    .then((res) => {
                        commit('setMinimumGrossMargin', res.data.MinimumGrossMargin || 0);
                        resolve();
                    })
                    .catch((error) => {
                        helpers.showFailNotification(
                            'An error occurred while fetching the minimum gross margin.',
                            { error }
                        );
                        reject(error);
                    });
            });
        },

        searchContacts({ commit, rootState, getters }, { query, limit }) {
            const top = `$top=${limit}`;
            const select = '$select=Id,Email,FirstName,LastName,ProfileImageUrl,CompanyId,Title';
            let filter = `$filter=ClientId eq ${getters.clientId}`;

            commit('setSecondaryContactsQuery', {
                query,
                limit
            });

            if (rootState.opportunity.company) {
                filter += ` and CompanyId eq ${rootState.opportunity.company.id}`;
            }

            if (query) {
                filter += ` and
                (
                    contains(concat(concat(FirstName, ' '), LastName), '${encodeURIComponent(query)}')
                    or contains(Email, '${encodeURIComponent(query)}')
                    or contains(Company/Name, '${encodeURIComponent(query)}')
                )`;
            }

            const queryParameters = `${filter}&${top}&${select}&$count=true`;
            const restUrl = `/service/Contacts?${queryParameters}`;

            api
                .get(restUrl)
                .then((response) => {
                    const items = [];

                    for (let i = 0; i < response.data.value.length; i++) {
                        const item = {
                            id: response.data.value[i].Id,
                            email: response.data.value[i].Email,
                            name:
                                (response.data.value[i].FirstName || '') +
                                ' ' +
                                (response.data.value[i].LastName || ''),
                            profilePicture:
                                response.data.value[i].ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                            companyId: response.data.value[i].CompanyId,
                            title: response.data.value[i].Title || null
                        };

                        items.push(item);
                    }

                    commit('setAvailableContacts', items);
                })
                .catch((error) => {
                    helpers.showFailNotification(
                        'An error occurred while fetching your contacts.',
                        { error }
                    );
                });
        },

        searchUsers({ commit, getters }, { query, limit }) {
            const sort = '$orderby=FirstName';
            let filter = `$filter=ClientId eq ${getters.clientId} and Roles/any(r: r/RoleId eq '6') and IsActive eq true`;

            if (query) {
                filter += ` and (
                        contains(concat(concat(FirstName, ' '), LastName), '${encodeURIComponent(query)}')
                        or contains(Email, '${encodeURIComponent(query)}')
                    )`;
            }

            const top = `$top=${limit}`;
            const restUrl = `/service/Users?${filter}&${top}&$count=true&${sort}`;

            api
                .get(restUrl)
                .then((response) => {
                    const items = [];
                    for (let i = 0; i < response.data.value.length; i++) {
                        const item = {
                            id: response.data.value[i].Id,
                            name: helpers.getName(
                                response.data.value[i].FirstName,
                                response.data.value[i].LastName
                            ),
                            profilePicture:
                                response.data.value[i].ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                            title: response.data.value[i].Title,
                            email: response.data.value[i].Email
                        };
                        items.push(item);
                    }

                    commit('setUsers', items);
                })
                .catch((error) => {
                    helpers.showFailNotification(
                        'An error occurred while fetching your sales professionals',
                        { error }
                    );
                });
        },

        setCompanyBySelectedContact({ commit }, contactCompanyId) {
            const select = '$select=Id,Name,LogoUrl';
            const filter = `$filter=Id eq ${contactCompanyId}`;
            const restUrl = `/service/Companies?${filter}&${select}`;

            api
                .get(restUrl)
                .then((response) => {
                    commit('updateOpportunity', (o) => {
                        o.company = {
                            id: response.data.value[0].Id,
                            name: response.data.value[0].Name,
                            imageUrl:
                                response.data.value[0].LogoUrl ||
                                require('@/assets/images/company-avatar.png')
                        };
                    });
                })
                .catch((error) => {
                    helpers.showFailNotification('Failed to get company', { error });
                });
        },

        getOpportunityLineItems({ rootState }, discountUnit) {
            return new Promise((resolve, reject) => {
                const opportunityId = rootState.opportunity.id;
                const expand = '$expand=LineItems($select=Id,BillingCycle,TermInMonths,UnitPrice,UnitCost,Quantity,UnitDiscount,ConnectWiseDealLineItemId,Solution;$expand=Solution($select=Id,Name,Description,Url,VideoUrl,Bundles,Categories;$expand=Categories($select=Id,Name),Bundles($select=Id,Name,Description,ImageUrl,Price,Quantity,FileMimeType)))';
                const select = '$select=LineItems';
                const query = `/service/Deals?$filter=Id eq ${opportunityId}&${expand}&${select}&$count=true`;

                api
                    .get(query)
                    .then((response) => {
                        if (!response.data?.value?.[0]?.LineItems || response.data.value[0].LineItems.length === 0) {
                            resolve([]);
                        } else {
                            const lineItems = OpportunityHelpers.getLineItems(response.data.value[0], discountUnit);
                            resolve(lineItems);
                            return lineItems;
                        }
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        loadOpportunityDetails({ state, rootState }) {
            const opportunityId = rootState.opportunity.id;
            if (!opportunityId) {
                return;
            }

            const select = '$select=Id,Name,Description,AccountId,Price,Cost,Discount,DiscountTypeId,Duration,CloseDate,CreatedAt,IsApproved,IsBelowThreshold,LifetimeValue';
            const expand = '$expand=RegisteredDeals($expand=Agency($select=Id,Name)),CreatedByUser($select=Id,FirstName,LastName,Email,ProfileImageUrl),DealStatus($select=Id,Name,Color),DealSalesPersons($expand=SalesPerson($select=FirstName,LastName,Email,ProfileImageUrl)),DealContacts($expand=Contact($select=Email,FirstName,LastName,ProfileImageUrl,CompanyId)),PriceType($select=Id,Name),DealType($select=Id,Name),Contact($expand=Company($select=Id,Name,LogoUrl))';

            return new Promise((resolve, reject) => {
                api
                .get(`/service/Deals?$filter=Id eq ${opportunityId}&${expand}&${select}`)
                .then((response) => {
                    const opportunityResponse = response.data.value[0];

                    const discountUnit = OpportunityHelpers.getDiscountUnit(opportunityResponse);

                    detailsTab.actions.getOpportunityLineItems({ rootState }, discountUnit).then((lineItems) => {
                        const isLineItemDiscountActive = !!lineItems.find(
                            ({ discount }) => !!discount
                        );
                        const discount = !isLineItemDiscountActive
                            ? opportunityResponse.Discount
                            : null;

                        const lifetimeValue = opportunityResponse.LifetimeValue;

                        const defaultOpportunity = createOpportunity();
    
                        const opportunity = {
                            accountId: opportunityResponse.AccountId,
                            name: opportunityResponse.Name,
                            description: opportunityResponse.Description,
                            netAmount: lifetimeValue,
                            total: lifetimeValue, // TODO: I believe this is redudant to netAmount, simpleEntry.totalPrice, and itemizedEntry.totalPrice
                            type: OpportunityHelpers.getType(opportunityResponse),
                            statusColor: opportunityResponse.DealStatus
                                ? opportunityResponse.DealStatus.Color
                                : null,
                            status: OpportunityHelpers.getStatus(opportunityResponse),
                            dateOpened: moment.utc(opportunityResponse.CreatedAt),
                            dateClosed: opportunityResponse.CloseDate
                                ? moment.utc(opportunityResponse.CloseDate).local().toDate()
                                : null,
                            daysOpen: OpportunityHelpers.getDaysOpen(opportunityResponse),
                            contact: OpportunityHelpers.getContact(opportunityResponse),
                            secondaryContacts: OpportunityHelpers.getSecondaryContacts(opportunityResponse),
                            company: OpportunityHelpers.getCompany(opportunityResponse),
                            salesperson: OpportunityHelpers.getSalesperson(opportunityResponse),
                            salespersons: OpportunityHelpers.getSalespersons(opportunityResponse),
                            registeredDeals: OpportunityHelpers.getRegisteredDeals(opportunityResponse),
                            priceType: opportunityResponse.PriceType
                                ? opportunityResponse.PriceType.Name
                                : null,
                            priceDuration: opportunityResponse.Duration || 0,
                            price: opportunityResponse.Price,
                            cost: opportunityResponse.Cost,
                            upfront: OpportunityHelpers.getUpfrontPayment(lineItems),
                            yearly: OpportunityHelpers.getAnnualPayment(lineItems),
                            discount,
                            discountUnit,
                            isApproved: opportunityResponse.IsApproved,
                            isBelowThreshold: opportunityResponse.IsBelowThreshold,
                            simpleEntry: {
                                ...defaultOpportunity.simpleEntry,
                                ...OpportunityHelpers.getSimpleEntry(opportunityResponse)
                            },
                            itemizedEntry: defaultOpportunity.itemizedEntry
                        };
    
                        if (!isEmpty(lineItems)) {
                            // Itemized Entry
                            opportunity.pricingDetails = 'itemized';
                            const hasItemDiscount = lineItems.some(
                                ({ discount }) => discount > 0
                            );
                            const discountType = hasItemDiscount
                                ? 'item'
                                : opportunityResponse.Discount
                                    ? 'total'
                                    : null;
    
                            if (discountType) {
                                opportunity.itemizedEntry.hasDiscount = true;
                                opportunity.itemizedEntry.discountType = discountType;
    
                                const discountTypeId = opportunityResponse.DiscountTypeId;
                                const matchingDiscount = state.discountOptions.find(
                                    ({ id }) => id === discountTypeId
                                ) || { value: 'percentage' };
    
                                // TODO: I believe this could eventually be cleaned up so we aren't passing in the same info twice
                                opportunity.itemizedEntry.itemDiscountValue =
                                    opportunityResponse.Discount; // Integer value e.g. 10
                                opportunity.itemizedEntry.totalDiscountValue =
                                    opportunityResponse.Discount; // Integer value e.g. 10
                                opportunity.itemizedEntry.itemDiscountType =
                                    matchingDiscount.value; // Will either be 'percentage' or 'amount'
                                opportunity.itemizedEntry.totalDiscountType =
                                    matchingDiscount.value; // Will either be 'percentage' or 'amount'
                            }
    
                            opportunity.itemizedEntry.solutions = lineItems;
                            opportunity.itemizedEntry.upfrontPayment = OpportunityHelpers.getUpfrontPayment(opportunity.itemizedEntry.solutions);
                            opportunity.itemizedEntry.annualPayment = OpportunityHelpers.getAnnualPayment(opportunity.itemizedEntry.solutions);
                            opportunity.itemizedEntry.totalPrice = OpportunityHelpers.getItemizedEntryTotalPrice(opportunity);
                            opportunity.itemizedEntry.totalCost = OpportunityHelpers.getItemizedEntryTotalCost(opportunity);
                            opportunity.itemizedEntry.preDiscountPrice = OpportunityHelpers.getItemizedEntryTotalPrice(opportunity, false); // This is the calulated total price without the discount
    
                            opportunity.discountAmount =  opportunity.itemizedEntry.preDiscountPrice - opportunity.itemizedEntry.totalPrice;
                            opportunity.discountPercentage = OpportunityHelpers.getDiscountPercentage(discount, discountUnit, opportunity.itemizedEntry.preDiscountPrice);
    
                            opportunity.grossMargin = {
                                amount: opportunity.itemizedEntry.totalPrice - opportunity.itemizedEntry.totalCost,
                                percentage: helpers.calculateGrossMarginPercentage(
                                    opportunity.itemizedEntry.totalPrice - opportunity.itemizedEntry.totalCost,
                                    opportunity.itemizedEntry.totalPrice
                                )
                            }
                        } else {
                            // Simple Entry
                            opportunity.simpleEntry.duration = opportunityResponse.Duration;
                            opportunity.simpleEntry.cost = opportunityResponse.Cost || 0;
                            opportunity.simpleEntry.discount = opportunityResponse.Discount || 0;
                            opportunity.simpleEntry.discountType = (
                                state.discountOptions.find(
                                    ({ id }) => id === opportunityResponse.DiscountTypeId
                                ) || { value: 'percentage' }
                            ).value;
    
                            opportunity.simpleEntry.price = opportunityResponse.Price;
    
                            opportunity.simpleEntry.price = opportunity.simpleEntry.price ?
                                opportunity.simpleEntry.price.toFixed(2) : 0;
                            opportunity.simpleEntry.cost = opportunity.simpleEntry.cost ?
                                opportunity.simpleEntry.cost.toFixed(2) : 0;
    
                            opportunity.simpleEntry.totalPrice = OpportunityHelpers.getSimpleEntryTotalPrice(opportunity);
                            opportunity.simpleEntry.totalCost = OpportunityHelpers.getSimpleEntryTotalCost(opportunity);
                            opportunity.simpleEntry.preDiscountPrice = OpportunityHelpers.getSimpleEntryTotalPrice(opportunity, false);
                            opportunity.discountAmount = opportunity.simpleEntry.preDiscountPrice - opportunity.simpleEntry.totalPrice;;
                            opportunity.discountPercentage = OpportunityHelpers.getDiscountPercentage(discount, discountUnit, opportunity.simpleEntry.preDiscountPrice);
    
                            opportunity.grossMargin = {
                                amount: opportunity.simpleEntry.totalPrice - opportunity.simpleEntry.totalCost,
                                percentage: helpers.calculateGrossMarginPercentage(
                                    opportunity.simpleEntry.totalPrice - opportunity.simpleEntry.totalCost,
                                    opportunity.simpleEntry.totalPrice
                                )
                            }
                        }
    
                        rootState.opportunity = {
                            ...rootState.opportunity,
                            ...opportunity
                        };

                        resolve();
                    }).catch((error) => {
                        reject(error);
                    });
                });
            });
        },
    }
};